import { useMemo } from 'react'

import { BarDatum } from '@nivo/bar'
import { Box } from '@nivo/core'
import { LineSeries } from '@nivo/line'
import { DefaultRawDatum } from '@nivo/pie'
import { PartialTheme } from '@nivo/theming'

import { ChartType } from 'src/components/Visualizations/util'
import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import { useTheme } from 'src/hooks/use-theme'

type Layout = 'horizontal' | 'vertical'

type BaseProps = {
  chartType: ChartType
  formatLeftAxisLabel?: (value: string) => string
  layout?: Layout
}

type BarChartProps = BaseProps & {
  chartType: ChartType.Bar
  data: readonly BarDatum[]
  getIndex: (bar?: BarDatum) => string
  keys: readonly string[]
}

type LineChartProps = BaseProps & {
  chartType: ChartType.Line
  data: readonly LineSeries[]
  hasLegend: boolean
}

type PieChartProps = BaseProps & {
  chartType: ChartType.Pie
  data: readonly DefaultRawDatum[]
}

type Props = BarChartProps | LineChartProps | PieChartProps

type BottomAxisFormatSettings = {
  bottomAxisLabelFormat: (value: string) => string | undefined // Formats the bottom axis label to truncate if it's too long
  bottomAxisLabelRotation: 0 | 45 | 90 | undefined // The rotation of the bottom axis label
  bottomAxisLegendOffset: number | undefined // The offset of the bottom legend
}

export type NivoChartFormatSettings = {
  bottomAxisFormatSettings: BottomAxisFormatSettings | undefined // The format settings for the bottom axis
  flexBasisPx: number // The flex basis of the chart
  isEmpty: boolean // Whether the chart has no content
  leftAxisLegendOffset: number // The offset of the left axis legend
  legendWidthPx: number | undefined // The width of the legend
  margins: Box // The margins of the chart
  minWidthPx: number // The minimum width of the chart
  theme: PartialTheme // The theme of the chart
  totalLegendWidthPx: number | undefined // The total width of the legend
}

const FONT_FAMILY = 'Public Sans'
const FONT_SIZE_PX = 11
const CHAR_HEIGHT_PX = 12

const PER_ITEM_WIDTH_PX = 65
const GAP_PX = 5
const CARD_PADDING_PX = 50
const MIN_WIDTH_PX = 300
const BAR_CHART_RIGHT_LABEL_CLIP_PADDING_PX = 15
const BOTTOM_LEGEND_MIN_OFFSET_PX = 32
const LEFT_AXIS_PADDING_EXTRA_PX = 10
const BOTTOM_LEGEND_PADDING_EXTRA_PX = 10
const ROTATION_THRESHOLD = 10

const BASE_LEGEND_WIDTH_PX = 25
const LEGEND_PADDING_PX = 15

const FIXED_PIE_CHART_WIDTH_PX = 250

export const measureMaxLabelWidth = (labels: readonly string[]): number => {
  if (labels.length === 0) {
    return 0
  }
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return 0
  }
  ctx.font = `${FONT_SIZE_PX}px ${FONT_FAMILY}`
  let max = 0
  for (const l of labels) {
    max = Math.max(max, ctx.measureText(l).width)
  }
  return max
}

// Calculate the horizontal width of the last label in the bottom axis
const rotatedLabelWidth = (
  xAxisKeys: readonly string[],
  bottomAxisFormatSettings: BottomAxisFormatSettings | undefined,
  bottomAxisLabelRotation: 0 | 45 | 90 | undefined
) => {
  const lastLabel = xAxisKeys.length > 0 ? (xAxisKeys[xAxisKeys.length - 1] ?? '') : ''
  const labelWidth = measureMaxLabelWidth([
    bottomAxisFormatSettings?.bottomAxisLabelFormat(lastLabel) ?? '',
  ])
  return bottomAxisLabelRotation === 45
    ? Math.cos(Math.PI / 4) * (labelWidth + CHAR_HEIGHT_PX)
    : labelWidth
}

// Bottom legend offset calculations accounting for rotation
const bottomAxisLegendOffset = (
  xAxisKeys: readonly string[],
  bottomAxisLabelFormat: (value: string) => string,
  bottomAxisLabelRotation: 0 | 45 | 90 | undefined
) => {
  if (bottomAxisLabelRotation === 0) {
    return BOTTOM_LEGEND_MIN_OFFSET_PX
  }

  const formatted = xAxisKeys.map((k: string) => bottomAxisLabelFormat(k))
  const widest = measureMaxLabelWidth(formatted)
  const rotatedOffset =
    bottomAxisLabelRotation === 45 ? Math.sin(Math.PI / 4) * (widest + CHAR_HEIGHT_PX) : widest
  const rotatedWithPadding = rotatedOffset + CHAR_HEIGHT_PX
  return Math.max(rotatedWithPadding, BOTTOM_LEGEND_MIN_OFFSET_PX)
}

// Computes the format settings for a chart based off of the chart type and data
export const useNivoChartFormatSettings = (props: Props): NivoChartFormatSettings => {
  const { layout = 'vertical', formatLeftAxisLabel, chartType } = props
  const { isSmallerThanOrEqual } = useCurrentBreakpoint()
  const isSmallScreen = isSmallerThanOrEqual('sm')

  /*
    Construct derived information from input data needed to compute format settings
  */

  // Compute xAxisKeys
  const xAxisKeys = useMemo<string[]>(() => {
    switch (chartType) {
      case ChartType.Bar: {
        const { data, getIndex } = props
        return data.map((d) => getIndex(d))
      }
      case ChartType.Line: {
        const { data } = props
        const set = new Set<string>()
        for (const series of data) {
          for (const point of series.data) {
            set.add(String(point.x))
          }
        }
        return Array.from(set)
      }
      case ChartType.Pie: // Pie charts don't have an x axis
      default:
        return []
    }
  }, [props, chartType])

  // Compute seriesKeys - series are the individual entities in the chart (EX: bar segment in a bar chart, line in a line chart, slice in a pie chart)
  const seriesKeys = useMemo<readonly string[]>(() => {
    switch (chartType) {
      case ChartType.Bar: {
        const { keys } = props
        return keys
      }
      case ChartType.Line: {
        const { data } = props
        return data.map((s) => String(s.id))
      }
      case ChartType.Pie: {
        const { data } = props
        return data.map((s) => String(s.id))
      }
      default:
        return []
    }
  }, [props, chartType])

  // Compute seriesValues
  const seriesValues = useMemo<number[]>(() => {
    switch (chartType) {
      case ChartType.Bar: {
        // For bar charts, the series values are the sum of the series values for each bar
        const { data, keys } = props
        return data.map((bar) => {
          return keys.reduce((sum: number, key: string) => {
            const value = bar[key]
            if (typeof value === 'number') {
              return sum + value
            }
            return sum
          }, 0)
        })
      }
      case ChartType.Line: {
        const { data } = props
        const maxByX = new Map<string, number>()
        for (const series of data) {
          for (const point of series.data) {
            const x = String(point.x)
            const prev = maxByX.get(x) ?? 0
            const yVal = typeof point.y === 'number' ? point.y : 0
            maxByX.set(x, Math.max(prev, yVal))
          }
        }
        return xAxisKeys.map((k) => maxByX.get(k) ?? 0)
      }
      case ChartType.Pie: {
        const { data } = props
        return data.map((s) => s.value)
      }
      default:
        return []
    }
  }, [props, xAxisKeys, chartType])

  /*
    Legend handling
  */

  // Accommodate space for a legend on the right if applicable
  const hasLegend = (chartType === ChartType.Line && props.hasLegend) || chartType === ChartType.Pie
  const longestLegendLabel = useMemo(() => {
    if (!hasLegend) {
      return 0
    }
    return measureMaxLabelWidth(seriesKeys)
  }, [hasLegend, seriesKeys])

  const legendWidthPx = hasLegend ? longestLegendLabel + BASE_LEGEND_WIDTH_PX : 0
  const totalLegendWidthPx = hasLegend ? legendWidthPx + LEGEND_PADDING_PX : 0

  /*
    Calculate chart label lengths to determine orientation and margins
  */

  // Rotation based on screen size and number of x labels
  const bottomAxisLabelRotation: 0 | 45 | 90 | undefined = useMemo(() => {
    if (chartType === ChartType.Pie) {
      return undefined
    }
    if (isSmallScreen) {
      return 90
    }
    return xAxisKeys.length >= ROTATION_THRESHOLD ? 45 : 0
  }, [isSmallScreen, xAxisKeys.length, chartType])

  const formattedMaxDataSize = useMemo(() => {
    const maxDataSize = Math.floor(Math.max(0, ...seriesValues))
    const raw = String(maxDataSize)
    return formatLeftAxisLabel ? formatLeftAxisLabel(raw) : raw
  }, [seriesValues, formatLeftAxisLabel])

  /*
    Left axis handling
  */

  const hasLeftAxis = chartType !== ChartType.Pie
  const leftAxisLegendOffset = useMemo(() => {
    if (!hasLeftAxis) {
      return 0
    }

    const measuredMaxLeftNumberWidth = measureMaxLabelWidth([formattedMaxDataSize])

    // left number width plus a character height and a small padding, mirroring BarChart
    const maxNumberOfDataDigitsOffset = measuredMaxLeftNumberWidth + CHAR_HEIGHT_PX + 10

    return -1 * Math.max(maxNumberOfDataDigitsOffset, 40)
  }, [formattedMaxDataSize, hasLeftAxis])

  /*
    Bottom axis handling
  */

  const hasBottomAxis = chartType !== ChartType.Pie

  const bottomAxisFormatSettings: BottomAxisFormatSettings | undefined = useMemo(() => {
    if (!hasBottomAxis) {
      return undefined
    }

    const maxLength = bottomAxisLabelRotation === 45 ? 15 : 10

    const longestIndexLength =
      layout === 'horizontal'
        ? formattedMaxDataSize.length
        : xAxisKeys.reduce((max: number, l: string) => Math.max(max, (l ?? '').length), 0)
    const bottomAxisLabelMaxLength = Math.min(maxLength, longestIndexLength)

    const bottomAxisLabelFormat = (value: string) => {
      if (value && value.length > bottomAxisLabelMaxLength) {
        return value.slice(0, bottomAxisLabelMaxLength - 2) + '...'
      }
      return value
    }

    return {
      bottomAxisLabelFormat,
      bottomAxisLabelRotation,
      bottomAxisLegendOffset: bottomAxisLegendOffset(
        xAxisKeys,
        bottomAxisLabelFormat,
        bottomAxisLabelRotation
      ),
    }
  }, [bottomAxisLabelRotation, formattedMaxDataSize.length, hasBottomAxis, layout, xAxisKeys])

  /*
    Calculate margins
  */

  // Right margin to avoid cutting off last 45° label
  const rightMargin = useMemo(() => {
    if (layout === 'horizontal') {
      return 10
    }

    /*
      - If there is a legend, we don't need to add extra margin to accommodate for the rightmost label
      - Label width only matters if the there is a bottom axis label
      - Bar charts labels are centered under the bar, so there's some extra space for the label to render
    */
    return Math.max(
      (hasLegend
        ? totalLegendWidthPx
        : hasBottomAxis
          ? rotatedLabelWidth(xAxisKeys, bottomAxisFormatSettings, bottomAxisLabelRotation)
          : 0) - (chartType === ChartType.Bar ? BAR_CHART_RIGHT_LABEL_CLIP_PADDING_PX : 0),
      0
    )
  }, [
    layout,
    hasLegend,
    totalLegendWidthPx,
    hasBottomAxis,
    chartType,
    xAxisKeys,
    bottomAxisFormatSettings,
    bottomAxisLabelRotation,
  ])

  const leftMargin = useMemo(() => {
    return leftAxisLegendOffset * -1 + LEFT_AXIS_PADDING_EXTRA_PX
  }, [leftAxisLegendOffset])

  const topMargin = 10
  const bottomMargin = useMemo(
    () => (bottomAxisFormatSettings?.bottomAxisLegendOffset ?? 0) + BOTTOM_LEGEND_PADDING_EXTRA_PX,
    [bottomAxisFormatSettings]
  )

  const margins = useMemo(
    () => ({ top: topMargin, right: rightMargin, bottom: bottomMargin, left: leftMargin }),
    [topMargin, rightMargin, bottomMargin, leftMargin]
  )

  const flexBasisPx = useMemo(() => {
    const dataLength = xAxisKeys.length
    const dataWidth =
      chartType === ChartType.Pie
        ? FIXED_PIE_CHART_WIDTH_PX
        : dataLength * PER_ITEM_WIDTH_PX + (dataLength > 0 ? (dataLength - 1) * GAP_PX : 0)

    const widthEstimate = dataWidth + leftMargin + rightMargin + CARD_PADDING_PX
    return Math.max(widthEstimate, MIN_WIDTH_PX)
  }, [chartType, leftMargin, rightMargin, xAxisKeys.length])

  const isEmpty = useMemo(() => {
    const hasKeys = seriesKeys.length > 0
    const maxVal = Math.max(0, ...seriesValues)
    return !hasKeys || maxVal === 0
  }, [seriesKeys.length, seriesValues])

  const { getTokenVal } = useTheme()
  const labelColor = getTokenVal('colorTextLabel')

  const theme: PartialTheme = useMemo(
    () => ({
      axis: {
        ticks: {
          text: {
            fill: labelColor,
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE_PX,
          },
        },
        legend: {
          text: {
            fill: labelColor,
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE_PX,
          },
        },
      },
      legends: {
        text: {
          fill: labelColor,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE_PX,
        },
      },
    }),
    [labelColor]
  )

  return {
    bottomAxisFormatSettings,
    leftAxisLegendOffset,
    margins,
    minWidthPx: MIN_WIDTH_PX,
    flexBasisPx,
    isEmpty,
    theme,
    totalLegendWidthPx,
    legendWidthPx,
  }
}

export default useNivoChartFormatSettings
